import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService';
import { getStructuresByProjectId } from '../../services/structureService';
import {
  getAvailableJurisdictions,
  PROJECT_SCOPES,
} from '../../data/buildingCodes';
import AddressInput from '../AddressInput';
import { Button, Card, Checkbox, Disclosure, Heading, Text } from '../ui';
import {
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_ORDER,
  CREATOR_ROUTES,
  getDocumentUrl,
} from './shared/DocumentTypeConfig';
import useDocumentCounts from './shared/useDocumentCounts';
import { deleteDocumentByCreatedAt } from './shared/documentStorageService';
import DocumentTypeCard from './DocumentTypeCard';
import DocumentList from './DocumentList';
import useProposalLinks from '../../features/proposal/useProposalLinks';
import { sentLine } from '../../features/proposal/proposalFormat';
import { useProjectAccess } from '../../hooks/useProjectAccess';
import ProposalLinkActions from '../../features/proposal/components/ProposalLinkActions';
import { openDocumentUrl } from '../../utils/documentsHostBridge';
import '../../styles/DocumentsHub.css';

export default function DocumentsHub({
  embedded = false,
  projectId: propProjectId,
} = {}) {
  const params = useParams();
  const projectId = propProjectId || params.projectId;
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeList, setActiveList] = useState(null); // docTypeKey when viewing list
  const [savingSettings, setSavingSettings] = useState(false);

  const counts = useDocumentCounts(project);
  // Viewers see documents; creating, deleting and link controls are the owner's.
  const { isViewOnly } = useProjectAccess(projectId);
  const proposalLinks = useProposalLinks(
    projectId,
    activeList === 'proposal' && !isViewOnly
  );
  const loadProjectData = useCallback(async () => {
    try {
      setLoading(true);
      const [projectData, structureData] = await Promise.all([
        projectService.getProject(projectId),
        getStructuresByProjectId(projectId),
      ]);
      setProject(projectData);
      setStructures(structureData);
    } catch (err) {
      console.error('Error loading project:', err);
      setError('Failed to load project data.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  const handleSaveBuildingCode = async (jurisdiction, scopes) => {
    try {
      setSavingSettings(true);
      await projectService.updateProject(projectId, { jurisdiction, scopes });
      setProject((prev) => ({ ...prev, jurisdiction, scopes }));
    } catch (err) {
      console.error('Failed to save building code settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  const jurisdictions = getAvailableJurisdictions();
  const hasScans = structures.length > 0;
  const hasAddress = !!(
    project?.address?.fullAddress ||
    project?.address?.streetAddress ||
    project?.parcelBoundary
  );

  const getDisabledState = (docTypeKey) => {
    const config = DOCUMENT_TYPES[docTypeKey];
    if (config.requiresScan && !hasScans) {
      return { disabled: true, reason: 'Requires at least one scan' };
    }
    if (config.requiresAddress && !hasAddress) {
      return { disabled: true, reason: 'Set your property address first' };
    }
    return { disabled: false, reason: null };
  };

  const handleAdd = (docTypeKey) => {
    const routeSegment = CREATOR_ROUTES[docTypeKey];
    navigate(`/project/${projectId}/documents/${routeSegment}/create`);
  };

  const handleView = (docTypeKey) => {
    setActiveList(docTypeKey);
  };

  const handleViewDocument = (doc) => {
    openDocumentUrl(getDocumentUrl(doc), { name: doc.name || doc.filename });
  };

  const renderProposalActions = (doc) => (
    <ProposalLinkActions
      doc={doc}
      enabled={proposalLinks.enabledByToken?.[doc.shareToken]}
      onRevoke={proposalLinks.revoke}
      note={sentLine(proposalLinks.sentByToken?.[doc.shareToken])}
    />
  );

  const handleDeleteDocument = async (createdAt) => {
    // A deleted proposal must not leave its online link live.
    if (activeList === 'proposal') {
      const doc = (project?.proposalDocs || []).find(
        (d) => d.createdAt === createdAt
      );
      const token = doc?.shareToken;
      if (token && proposalLinks.enabledByToken?.[token] !== false) {
        await proposalLinks.revoke(token);
      }
    }
    await deleteDocumentByCreatedAt(projectId, activeList, createdAt);
    await loadProjectData();
  };

  if (loading) {
    return (
      <div className="documents-hub">
        <Text tone="dark" variant="subtle" className="documents-hub__status">
          Loading documents...
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="documents-hub">
        <Text
          tone="dark"
          className="documents-hub__status documents-hub__status--error"
        >
          {error}
        </Text>
      </div>
    );
  }

  // Show document list for a specific type
  if (activeList) {
    return (
      <div className="documents-hub">
        <DocumentList
          projectData={project}
          docTypeKey={activeList}
          onView={handleViewDocument}
          onDelete={isViewOnly ? null : handleDeleteDocument}
          onBack={() => setActiveList(null)}
          onAdd={isViewOnly ? null : () => handleAdd(activeList)}
          renderItemActions={
            activeList === 'proposal' && !isViewOnly
              ? renderProposalActions
              : null
          }
        />
      </div>
    );
  }

  const summaryText =
    project?.address?.fullAddress ||
    project?.address?.streetAddress ||
    'No address set';
  const jurisdictionText = project?.jurisdiction
    ? ` · ${jurisdictions.find((j) => j.id === project.jurisdiction)?.name || project.jurisdiction}`
    : '';

  // Main hub view
  return (
    <div
      className={`documents-hub ${embedded ? 'documents-hub--embedded' : ''}`}
    >
      {!embedded && (
        <div className="documents-hub__header">
          <Button
            variant="ghost"
            tone="dark"
            size="sm"
            onClick={() => navigate(`/project/${projectId}`)}
            className="documents-hub__back-btn"
          >
            &larr; Back to Project
          </Button>
          <Heading level={2} tone="dark">
            Documents
          </Heading>
          <Text tone="dark" variant="subtle" size="sm">
            {project?.name || 'Project'} &mdash; Create and manage construction
            document elements
          </Text>
        </div>
      )}

      {/* Project Info Section: someone who may only view the project reads
          it — the rules would refuse the write after the screen offered it */}
      <Card
        variant="glass"
        padding="md"
        className="documents-hub__project-info"
      >
        {isViewOnly ? (
          <div className="documents-hub__info-readonly">
            <Heading level={4} tone="dark">
              Project Info
            </Heading>
            <Text tone="dark" variant="subtle" size="sm">
              {summaryText}
              {jurisdictionText}
            </Text>
          </div>
        ) : (
          <Disclosure
            title="Project Info"
            subtitle={`${summaryText}${jurisdictionText}`}
            tone="dark"
            variant="flat"
          >
            <div className="documents-hub__info-body">
              {/* Address */}
              <div className="documents-hub__info-section">
                <Heading level={4} tone="dark">
                  Property Address
                </Heading>
                <AddressInput
                  projectId={projectId}
                  currentAddress={project?.address}
                  onAddressUpdate={async () => {
                    await loadProjectData();
                  }}
                />
              </div>

              {/* Jurisdiction */}
              <div className="documents-hub__info-section">
                <Heading level={4} tone="dark">
                  Jurisdiction
                </Heading>
                <select
                  className="doc-creator__select"
                  value={project?.jurisdiction || 'city-of-la'}
                  onChange={(e) =>
                    handleSaveBuildingCode(
                      e.target.value,
                      project?.scopes || ['full-remodel']
                    )
                  }
                  disabled={savingSettings}
                >
                  {jurisdictions.map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project Scope */}
              <div className="documents-hub__info-section">
                <Heading level={4} tone="dark">
                  Project Scope
                </Heading>
                <div className="doc-creator__checkbox-group">
                  {PROJECT_SCOPES.map((scope) => (
                    <Checkbox
                      key={scope.id}
                      tone="dark"
                      checked={(project?.scopes || ['full-remodel']).includes(
                        scope.id
                      )}
                      disabled={savingSettings}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const current = project?.scopes || ['full-remodel'];
                        let next = checked
                          ? [...current, scope.id]
                          : current.filter((s) => s !== scope.id);
                        if (next.length === 0) next = ['full-remodel'];
                        handleSaveBuildingCode(
                          project?.jurisdiction || 'city-of-la',
                          next
                        );
                      }}
                    >
                      <span className="doc-creator__checkbox-label">
                        {scope.name}
                      </span>
                      <span className="doc-creator__checkbox-desc">
                        {scope.description}
                      </span>
                    </Checkbox>
                  ))}
                </div>
              </div>
            </div>
          </Disclosure>
        )}
      </Card>

      <div className="documents-hub__grid">
        {DOCUMENT_TYPE_ORDER.map((key) => {
          const config = DOCUMENT_TYPES[key];
          const { disabled, reason } = getDisabledState(key);
          return (
            <DocumentTypeCard
              key={key}
              config={config}
              count={counts[key]}
              onAdd={isViewOnly ? null : () => handleAdd(key)}
              onView={() => handleView(key)}
              disabled={disabled}
              disabledReason={reason}
            />
          );
        })}
      </div>
    </div>
  );
}
